import { Token } from '@kodiak-finance/sdk-core'
import { FeeAmount, nearestUsableTick, TICK_SPACINGS, TickMath, tickToPrice } from '@kodiak-finance/v3-sdk'
import { ButtonGray } from 'components/Button'
import { OutlineCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Minus, Plus } from 'react-feather'
import { tryParseTick } from 'state/mint/v3/utils'
import styled, { keyframes } from 'styled-components'
import { ThemedText } from 'theme'

import { Input as NumericalInput } from '../NumericalInput'

const pulse = (color: string) => keyframes`
  0% {
    box-shadow: 0 0 0 0 ${color};
  }

  70% {
    box-shadow: 0 0 0 2px ${color};
  }

  100% {
    box-shadow: 0 0 0 0 ${color};
  }
`

const InputRow = styled.div`
  display: flex;
  width: 100%;
`

const TitleRow = styled.div<{ locked?: boolean }>`
  display: grid;
  grid-template-columns: ${({ locked }) => (locked ? '1fr' : '30px 1fr 30px')};
  align-items: center;
`

const SmallButton = styled(ButtonGray)`
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  background: ${({ theme }) => theme.cardPrimary};
  padding: 4px;
`

const FocusedOutlineCard = styled(OutlineCard)<{ active?: boolean; pulsing?: boolean }>`
  border-color: ${({ active, theme }) => (active ? theme.deprecated_blue1 : theme.cardBorderPrimary)};
  border-radius: 8px;
  min-height: 105px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px;
  background: ${({ theme }) => theme.cardSecondary};
  animation: ${({ pulsing, theme }) => pulsing && pulse(theme.deprecated_blue1)} 0.8s linear;
`

const StyledInput = styled(NumericalInput)<{ usePercent?: boolean }>`
  background-color: transparent;
  text-align: center;
  width: 100%;
  font-weight: 500;
  padding: 0 10px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 16px;
  `};

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    font-size: 12px;
  `};
`

const InputTitle = styled(ThemedText.DeprecatedSmall)`
  color: ${({ theme }) => theme.deprecated_text2};
  font-size: 12px;
  font-weight: 500;
  text-wrap: no-wrap;
`

const ButtonLabel = styled(ThemedText.DeprecatedWhite)<{ disabled: boolean }>`
  height: 16px;
  width: 16px;
  line-height: 16px;
  color: ${({ theme, disabled }) => (disabled ? theme.deprecated_text2 : theme.deprecated_text1)} !important;
`

interface StepCounterProps {
  // NEW: Tick-based props
  tick: number | undefined
  onTickInput: (tick: number | undefined) => void
  incrementTick: () => void
  decrementTick: () => void

  baseToken?: Token
  quoteToken?: Token
  feeAmount?: FeeAmount
  invertPrice?: boolean

  decrementDisabled?: boolean
  incrementDisabled?: boolean
  width?: string
  locked?: boolean
  title: ReactNode
  tokenA: string | undefined
  tokenB: string | undefined
}
const StepCounter = ({
  tick,
  onTickInput,
  incrementTick,
  decrementTick,
  baseToken,
  quoteToken,
  feeAmount,
  invertPrice = false,
  decrementDisabled = false,
  incrementDisabled = false,
  width,
  locked,
  title,
  tokenA,
  tokenB,
}: StepCounterProps) => {
  const [active, setActive] = useState(false)
  const [pulsing, setPulsing] = useState<boolean>(false)
  const [localValue, setLocalValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  const prevTickRef = useRef<number | undefined>(tick)

  const displayValue = useMemo(() => {
    if (typeof tick !== 'number' || !baseToken || !quoteToken) return ''
    if (feeAmount) {
      const tickMin = nearestUsableTick(TickMath.MIN_TICK, TICK_SPACINGS[feeAmount])
      const tickMax = nearestUsableTick(TickMath.MAX_TICK, TICK_SPACINGS[feeAmount])

      if (!invertPrice) {
        if (tick === tickMin) return '0'
        if (tick === tickMax) return '∞'
      } else {
        if (tick === tickMin) return '∞'
        if (tick === tickMax) return '0'
      }
    }

    try {
      const pCanon = tickToPrice(baseToken, quoteToken, tick)
      const pDisp = invertPrice ? pCanon.invert() : pCanon
      return pDisp.toSignificant(8)
    } catch {
      return ''
    }
  }, [tick, baseToken, quoteToken, feeAmount, invertPrice])

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(displayValue)

      if (tick !== prevTickRef.current && typeof tick === 'number') {
        setPulsing(true)
        setTimeout(() => setPulsing(false), 800)
      }
    }
    prevTickRef.current = tick
  }, [displayValue, tick, isEditing])

  const handleOnFocus = useCallback(() => {
    setIsEditing(true)
    setActive(true)
    setLocalValue(displayValue)
  }, [displayValue])

  const handleOnBlur = useCallback(() => {
    setIsEditing(false)
    setActive(false)
    if (localValue === '') {
      onTickInput(undefined)
      return
    }
    if (baseToken && quoteToken && feeAmount) {
      const baseForParse = invertPrice ? quoteToken : baseToken
      const quoteForParse = invertPrice ? baseToken : quoteToken
      const parsedTick = tryParseTick(baseForParse, quoteForParse, feeAmount, localValue)
      if (parsedTick !== undefined) onTickInput(parsedTick)
      else setLocalValue(displayValue)
    }
  }, [localValue, onTickInput, baseToken, quoteToken, feeAmount, invertPrice, displayValue])

  const handleLocalInput = useCallback(
    (val: string) => {
      if (isEditing) {
        setLocalValue(val)
      }
    },
    [isEditing]
  )

  const handleDecrement = useCallback(() => {
    setIsEditing(false)
    decrementTick()
  }, [decrementTick])

  const handleIncrement = useCallback(() => {
    setIsEditing(false)
    incrementTick()
  }, [incrementTick])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        setIsEditing(false)
        if (localValue === '') onTickInput(undefined)
        else if (baseToken && quoteToken && feeAmount) {
          const baseForParse = invertPrice ? quoteToken : baseToken
          const quoteForParse = invertPrice ? baseToken : quoteToken
          const parsedTick = tryParseTick(baseForParse, quoteForParse, feeAmount, localValue)
          onTickInput(parsedTick)
        }
        ;(e.target as HTMLInputElement).blur()
      }
      if (e.key === 'Escape') {
        setIsEditing(false)
        setLocalValue(displayValue)
        ;(e.target as HTMLInputElement).blur()
      }
    },
    [localValue, onTickInput, baseToken, quoteToken, feeAmount, invertPrice, displayValue]
  )

  return (
    <FocusedOutlineCard pulsing={pulsing} active={active} width={width}>
      <AutoColumn gap="6px">
        <TitleRow locked={locked}>
          {!locked && (
            <SmallButton onClick={handleDecrement} disabled={decrementDisabled} onMouseDown={(e) => e.preventDefault()}>
              <ButtonLabel disabled={decrementDisabled} fontSize="12px">
                <Minus size={16} />
              </ButtonLabel>
            </SmallButton>
          )}
          <InputTitle fontSize={12} textAlign="center">
            {title}
          </InputTitle>
          {!locked && (
            <SmallButton onClick={handleIncrement} disabled={incrementDisabled} onMouseDown={(e) => e.preventDefault()}>
              <ButtonLabel disabled={incrementDisabled} fontSize="12px">
                <Plus size={16} />
              </ButtonLabel>
            </SmallButton>
          )}
        </TitleRow>

        <InputRow>
          <StyledInput
            className="rate-input-0"
            value={localValue}
            fontSize="20px"
            disabled={locked}
            onUserInput={handleLocalInput}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            onKeyDown={handleKeyDown}
          />
        </InputRow>

        <InputTitle fontSize={12} textAlign="center">
          {tokenB} per {tokenA}
        </InputTitle>
      </AutoColumn>
    </FocusedOutlineCard>
  )
}

export default StepCounter
